import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';
import DrawdownBar from '../components/ui/DrawdownBar';
import { useDOA } from '../hooks/useDOA';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/sales-orders`;
const CONTRACTS_API = `${API_V1}/contracts`;
const CUSTOMERS_API = `${API_V1}/customer-master`;
const PAGE_SIZE = 50;

interface SOLine { id: number; line_number: number; product_code: string; description: string; quantity: number; unit_price: number; discount_pct: number; net_amount: number; gl_account: string; obligation_id?: number | null; }
interface SO { id: number; so_number: string; customer_id: string; customer_name: string; total_amount: number; lifecycle_status: string; entity_code: string; currency: string; contract_id?: number | null; contract_number?: string | null; credit_check_passed: boolean | null; credit_check_message: string | null; version: number; created_at: string; lines: SOLine[]; }
interface Summary { total: number; draft: number; confirmed: number; shipped: number; delivered: number; invoiced: number; closed: number; cancelled: number; open_amount: number; }
interface CreditCheck { customer_id: string; credit_limit: number; outstanding_ar: number; open_so_total: number; total_exposure: number; headroom: number; passed: boolean; message: string; }

// Phase 3c — MSA draw-down types
interface MSAContract {
  id: number;
  contract_number: string;
  title: string | null;
  contract_subtype: string | null;
  committed_amount: number | null;
  consumed_amount: number | null;
  currency: string | null;
  entity_code: string | null;
  counterparty_id: string | null;
  lifecycle_status: string;
}
interface RateCardItem {
  id: number;
  item_code: string;
  description: string;
  unit_price: number;
  unit_of_measure: string | null;
  gl_account: string | null;
  currency: string;
}
interface DrawdownPreview {
  committed: number;
  consumed_before: number;
  consumed_after: number;
  pct_used: number;
  warning: string | null;
  overage?: number;
  pobs_created?: number;
  schedules_created?: number;
  revenue_contract_id?: number;
  contract_number?: string;
}

interface CustomerLite {
  id: number;
  customer_id: string;
  customer_name: string | null;
  lifecycle_status: string | null;
  currency: string | null;
}

interface SOHistoryEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  transitioned_by: number;
  transitioned_by_email: string | null;
  transitioned_by_name: string | null;
  transitioned_by_doa_level: string | null;
  so_amount: number;
  transitioned_at: string | null;
}

export default function SalesOrders() {
  const navigate = useNavigate();
  const { canApprove } = useDOA();
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [sos, setSOs] = useState<SO[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCredit, setShowCredit] = useState<CreditCheck | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ customer_id: '', customer_name: '', entity_code: '', currency: 'USD', contract_id: '', lines: [{ product_code: '', description: '', quantity: '', unit_price: '', discount_pct: '', gl_account: '' }] });

  // Phase 3c — MSA draw-down state
  const [msaContracts, setMSAContracts] = useState<MSAContract[]>([]);
  const [msaRateCards, setMSARateCards] = useState<Record<number, RateCardItem[]>>({});
  const [drawdownPreview, setDrawdownPreview] = useState<DrawdownPreview | null>(null);
  const [drawdownConfirm, setDrawdownConfirm] = useState<DrawdownPreview | null>(null);
  // Customer dropdown state
  const [customers, setCustomers] = useState<CustomerLite[]>([]);
  // Per-SO audit trail cache (loaded lazily on row expand)
  const [histories, setHistories] = useState<Record<number, SOHistoryEntry[]>>({});

  const selectedMSA: MSAContract | null = form.contract_id
    ? msaContracts.find(c => c.id === parseInt(form.contract_id)) || null
    : null;
  const selectedRateCard: RateCardItem[] = selectedMSA
    ? (msaRateCards[selectedMSA.id] || [])
    : [];

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setSOs(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, entity]);

  useEffect(() => { load(); }, [load]);

  // Phase 3c — load MSA contracts + customer list when Create SO modal opens
  useEffect(() => {
    if (!showCreate) return;
    (async () => {
      try {
        const [msaRes, custRes] = await Promise.all([
          authFetch(`${CONTRACTS_API}?limit=200`),
          authFetch(`${CUSTOMERS_API}?limit=200`),
        ]);
        if (msaRes.ok) {
          const data = await msaRes.json();
          const items: MSAContract[] = (data.items || [])
            .filter((c: any) =>
              (c.contract_subtype || '').toUpperCase() === 'MSA_VALUE'
              && (c.committed_amount || 0) > 0
              && ['APPROVED', 'ACTIVE'].includes(c.lifecycle_status),
            );
          setMSAContracts(items);
        }
        if (custRes.ok) {
          const data = await custRes.json();
          setCustomers(data.items || []);
        }
      } catch (e) { console.error('Failed to load MSAs / customers', e); }
    })();
  }, [showCreate]);

  // Lazy-load rate card when an MSA is selected
  useEffect(() => {
    if (!form.contract_id) return;
    const cid = parseInt(form.contract_id);
    if (msaRateCards[cid]) return; // already loaded
    (async () => {
      try {
        const res = await authFetch(`${CONTRACTS_API}/extraction/${cid}`);
        if (!res.ok) return;
        const data = await res.json();
        setMSARateCards(prev => ({ ...prev, [cid]: data.price_list || [] }));
      } catch (e) { console.error('Failed to load rate card', e); }
    })();
  }, [form.contract_id, msaRateCards]);

  const resetForm = () => setForm({ customer_id: '', customer_name: '', entity_code: '', currency: 'USD', contract_id: '', lines: [{ product_code: '', description: '', quantity: '', unit_price: '', discount_pct: '', gl_account: '' }] });

  const handleCreate = async () => {
    try {
      const payload: any = {
        // so_number intentionally omitted — backend auto-generates SO-YYYYMMDD-NNNN
        customer_id: form.customer_id || null,
        customer_name: form.customer_name || null,
        entity_code: form.entity_code,
        currency: form.currency,
        contract_id: form.contract_id ? parseInt(form.contract_id) : null,
        lines: form.lines
          .filter(l => l.product_code || l.description)
          .map(l => ({
            product_code: l.product_code,
            description: l.description,
            quantity: parseFloat(l.quantity) || 0,
            unit_price: parseFloat(l.unit_price) || 0,
            discount_pct: parseFloat(l.discount_pct) || 0,
            gl_account: l.gl_account,
          })),
      };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        const data = await res.json();
        if (data.drawdown) {
          setDrawdownPreview(data.drawdown);
        }
        showToast(`Sales order created (SO #${data.so_number})`, true);
        setShowCreate(false);
        resetForm();
        load();
      } else {
        const e = await res.json();
        showToast(typeof e.detail === 'string' ? e.detail : (e.detail?.message || 'Error'), false);
      }
    } catch { showToast('Failed', false); }
  };

  const handleAction = async (id: number, action: string, version: number) => {
    try {
      const res = await authFetch(`${API}/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: version }) });
      if (res.ok) {
        // Phase 3c — capture drawdown from confirm response
        if (action === 'confirm') {
          try {
            const data = await res.json();
            if (data.drawdown) setDrawdownConfirm(data.drawdown);
          } catch { /* ignore */ }
        }
        showToast(`SO ${action}d successfully`, true);
        load();
      }
      else { const e = await res.json(); showToast(typeof e.detail === 'string' ? e.detail : (e.detail?.message || 'Error'), false); }
    } catch { showToast('Action failed', false); }
  };

  const handleCreditCheck = async (customerId: string) => {
    try {
      const res = await authFetch(`${API}/credit-check/${customerId}`);
      if (res.ok) { setShowCredit(await res.json()); }
      else { showToast('Credit check failed', false); }
    } catch { showToast('Credit check error', false); }
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, { product_code: '', description: '', quantity: '', unit_price: '', discount_pct: '', gl_account: '' }] });
  const updateLine = (i: number, field: string, value: string) => { const lines = [...form.lines]; (lines[i] as any)[field] = value; setForm({ ...form, lines }); };

  const loadHistory = useCallback(async (soId: number) => {
    try {
      const res = await authFetch(`${API}/${soId}/history`);
      if (!res.ok) return;
      const data = await res.json();
      setHistories(prev => ({ ...prev, [soId]: data.history || [] }));
    } catch (e) { console.error('Failed to load SO history', e); }
  }, []);

  const toggleExpand = (id: number) => {
    const newId = expandedId === id ? null : id;
    setExpandedId(newId);
    if (newId !== null && !histories[newId]) loadHistory(newId);
  };

  const handleDownloadPdf = async (soId: number, soNumber: string) => {
    try {
      const res = await authFetch(`${API}/${soId}/pdf`);
      if (!res.ok) { showToast('PDF generation failed', false); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${soNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); showToast('PDF download failed', false); }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const thStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap' };
  const tdStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };

  return (
    <PageLayout
      title="Sales Orders"
      subtitle="Create, approve, and fulfill sales orders for customers"
      action={{ label: '+ New SO', onClick: () => setShowCreate(true) }}
    >
      {summary && (
        <KpiGrid items={[
          { label: 'Total SOs', value: summary.total, color: 'var(--text-primary)' },
          { label: 'Draft', value: summary.draft, color: 'var(--text-secondary)' },
          { label: 'Confirmed', value: summary.confirmed, color: 'var(--accent)' },
          { label: 'Shipped', value: summary.shipped, color: 'var(--warning)' },
          { label: 'Delivered', value: summary.delivered, color: 'var(--positive)' },
          { label: 'Invoiced', value: summary.invoiced, color: 'var(--positive)' },
          { label: 'Closed', value: summary.closed, color: 'var(--text-secondary)' },
          { label: 'Cancelled', value: summary.cancelled, color: 'var(--negative)' },
          { label: 'Open Amount', value: formatAmountCompact(summary.open_amount), color: 'var(--accent)' },
        ]} />
      )}

      <FilterBar filters={[
        { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, width: 200, options: ['DRAFT', 'CONFIRMED', 'SHIPPED', 'DELIVERED', 'INVOICED', 'CLOSED', 'CANCELLED'].map(s => ({ value: s, label: s })) },
      ]} />

      {/* Expandable rows table — manual table with DataTable-consistent styles */}
      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : sos.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No sales orders found.</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...thStyle, textAlign: 'left' }}>SO #</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Customer</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Contract</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Entity</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Credit</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Status</th>
              <th style={{ ...thStyle, textAlign: 'left' }}>Actions</th>
            </tr></thead>
            <tbody>
              {sos.map(so => (
                <>
                  <tr key={so.id} onClick={() => toggleExpand(so.id)} style={{ cursor: 'pointer', background: expandedId === so.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent' }}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{so.so_number}</td>
                    <td style={tdStyle}>{so.customer_name || so.customer_id}</td>
                    <td style={{ ...tdStyle, fontSize: 11 }} onClick={e => e.stopPropagation()}>
                      {so.contract_id ? (
                        <a
                          href={`/contracts/${so.contract_id}`}
                          onClick={e => { e.preventDefault(); navigate(`/contracts/${so.contract_id}`); }}
                          style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}
                          title={so.contract_number || `Contract #${so.contract_id}`}
                        >
                          {so.contract_number || `#${so.contract_id}`}
                        </a>
                      ) : (
                        <span style={{ color: 'var(--text-hint)' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(so.total_amount)}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{so.entity_code}</td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      {so.credit_check_passed === true && <span style={{ color: 'var(--positive)', fontSize: 10, fontWeight: 600 }}>PASS</span>}
                      {so.credit_check_passed === false && <span style={{ color: 'var(--negative)', fontSize: 10, fontWeight: 600, cursor: 'pointer' }} title={so.credit_check_message || ''}>FAIL</span>}
                      {so.credit_check_passed === null && <ActionButton label="Check" onClick={() => handleCreditCheck(so.customer_id)} variant="ghost" size="sm" />}
                    </td>
                    <td style={tdStyle}><StatusBadge status={so.lifecycle_status} /></td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {so.lifecycle_status === 'DRAFT' && (canApprove(so.total_amount || 0, so.entity_code, 'SO') ? <ActionButton label="Confirm" onClick={() => handleAction(so.id, 'confirm', so.version)} variant="secondary" size="sm" /> : <ActionButton label="Confirm" onClick={() => {}} variant="secondary" size="sm" disabled title="Exceeds your approval authority" />)}
                        {so.lifecycle_status === 'CONFIRMED' && <ActionButton label="Ship" onClick={() => handleAction(so.id, 'ship', so.version)} variant="primary" size="sm" />}
                        {so.lifecycle_status === 'SHIPPED' && <ActionButton label="Deliver" onClick={() => handleAction(so.id, 'deliver', so.version)} variant="success" size="sm" />}
                        {so.lifecycle_status === 'DELIVERED' && <ActionButton label="Invoice" onClick={() => handleAction(so.id, 'invoice', so.version)} variant="primary" size="sm" />}
                        {so.lifecycle_status === 'INVOICED' && <ActionButton label="Close" onClick={() => handleAction(so.id, 'close', so.version)} variant="secondary" size="sm" />}
                        {['DRAFT', 'CONFIRMED'].includes(so.lifecycle_status) && <ActionButton label="Cancel" onClick={() => handleAction(so.id, 'cancel', so.version)} variant="danger" size="sm" />}
                        <ActionButton label="PDF" onClick={() => handleDownloadPdf(so.id, so.so_number)} variant="ghost" size="sm" title="Download SO PDF" />
                      </div>
                    </td>
                  </tr>
                  {expandedId === so.id && so.lines && (
                    <tr key={`${so.id}-lines`}><td colSpan={8} style={{ padding: '0 14px 12px 32px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 8 }}>
                        <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          {['#', 'Product', 'Description', 'Qty', 'Price', 'Disc %', 'Net', 'GL', 'POB'].map(h => (
                            <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 9, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>{so.lines.map(l => (
                          <tr key={l.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                            <td style={{ padding: '4px 8px' }}>{l.line_number}</td>
                            <td style={{ padding: '4px 8px' }}>{l.product_code || '-'}</td>
                            <td style={{ padding: '4px 8px' }}>{l.description}</td>
                            <td style={{ padding: '4px 8px' }}>{l.quantity}</td>
                            <td style={{ padding: '4px 8px' }}>${l.unit_price?.toFixed(2)}</td>
                            <td style={{ padding: '4px 8px' }}>{l.discount_pct}%</td>
                            <td style={{ padding: '4px 8px', fontWeight: 600 }}>${l.net_amount?.toFixed(2)}</td>
                            <td style={{ padding: '4px 8px', color: 'var(--text-secondary)' }}>{l.gl_account || '-'}</td>
                            <td style={{ padding: '4px 8px', color: l.obligation_id ? 'var(--accent)' : 'var(--text-hint)', fontWeight: l.obligation_id ? 600 : 400 }}>{l.obligation_id ? `#${l.obligation_id}` : '-'}</td>
                          </tr>
                        ))}</tbody>
                      </table>

                      {/* Phase 3c-ext: audit trail */}
                      <div style={{ marginTop: 12, fontSize: 9, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
                        Audit Trail
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 4 }}>
                        <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          {['When', 'From', 'To', 'By', 'DOA', 'Amount'].map(h => (
                            <th key={h} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 9, fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase' as const }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {(histories[so.id] || []).length === 0 ? (
                            <tr>
                              <td colSpan={6} style={{ padding: '6px 8px', color: 'var(--text-hint)', fontStyle: 'italic' }}>
                                {histories[so.id] ? 'No audit entries' : 'Loading...'}
                              </td>
                            </tr>
                          ) : (
                            (histories[so.id] || []).map(h => (
                              <tr key={h.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 10 }}>
                                  {h.transitioned_at ? h.transitioned_at.slice(0, 19).replace('T', ' ') : '-'}
                                </td>
                                <td style={{ padding: '4px 8px', color: 'var(--text-secondary)' }}>{h.from_status || '—'}</td>
                                <td style={{ padding: '4px 8px', fontWeight: 600, color: 'var(--accent)' }}>{h.to_status}</td>
                                <td style={{ padding: '4px 8px' }}>
                                  {h.transitioned_by_name || h.transitioned_by_email || `user ${h.transitioned_by}`}
                                </td>
                                <td style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700 }}>
                                  {h.transitioned_by_doa_level || '-'}
                                </td>
                                <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>
                                  {formatAmount(h.so_amount)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </td></tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', alignSelf: 'center' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      {/* Create SO Modal */}
      <Modal open={showCreate} onClose={() => { setShowCreate(false); resetForm(); }} title="Create Sales Order" size="lg" footer={<><button onClick={() => { setShowCreate(false); resetForm(); }} style={{ padding: '6px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Cancel</button><button onClick={handleCreate} style={{ padding: '6px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Create SO</button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <select value={form.entity_code} onChange={e => setForm({ ...form, entity_code: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select Legal Entity *</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} — {e.entity_name} ({e.functional_currency})</option>)}
          </select>

          {/* Phase 3c: Source contract (MSA) selector */}
          <div style={{ borderRadius: 6, background: selectedMSA ? 'rgba(139, 92, 246, 0.06)' : 'var(--bg-card)', border: selectedMSA ? '1px solid rgb(139, 92, 246)' : '1px solid var(--border-subtle)', padding: 10 }}>
            <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 6 }}>
              Source Contract (optional)
            </label>
            <select
              title="Source contract"
              aria-label="Source contract (optional)"
              value={form.contract_id}
              onChange={e => {
                const newCid = e.target.value;
                // When toggling contract: reset lines, auto-fill customer + entity + currency
                // from the MSA so the user doesn't have to hunt for it.
                const msa = newCid ? msaContracts.find(c => c.id === parseInt(newCid)) : null;
                setForm({
                  ...form,
                  contract_id: newCid,
                  customer_id: msa?.counterparty_id || form.customer_id,
                  customer_name: (() => {
                    if (!msa?.counterparty_id) return form.customer_name;
                    const c = customers.find(cc => cc.customer_id === msa.counterparty_id);
                    return c?.customer_name || form.customer_name;
                  })(),
                  entity_code: msa?.entity_code || form.entity_code,
                  currency: msa?.currency || form.currency,
                  lines: [{ product_code: '', description: '', quantity: '', unit_price: '', discount_pct: '', gl_account: '' }],
                });
              }}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">— None (standalone SO) —</option>
              {msaContracts.map(c => (
                <option key={c.id} value={c.id}>
                  {c.contract_number} • MSA • {c.currency || 'USD'} {formatAmount(c.committed_amount || 0)}
                  {' '} — consumed {formatAmount(c.consumed_amount || 0)}
                </option>
              ))}
            </select>
            {selectedMSA && (
              <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-secondary)' }}>
                Drawing against <strong>{selectedMSA.contract_number}</strong>. Line items will be validated against the MSA rate card; prices auto-fill from the price list.
              </div>
            )}
          </div>

          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>
              Customer *
            </label>
            <select
              title="Customer"
              aria-label="Customer"
              value={form.customer_id}
              onChange={e => {
                const cid = e.target.value;
                const c = customers.find(cc => cc.customer_id === cid);
                setForm({
                  ...form,
                  customer_id: cid,
                  customer_name: c?.customer_name || '',
                });
              }}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              <option value="">— Select customer —</option>
              {customers.map(c => (
                <option key={c.id} value={c.customer_id}>
                  {c.customer_name || c.customer_id} ({c.customer_id})
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
            <input
              placeholder="Currency"
              value={form.currency}
              onChange={e => setForm({ ...form, currency: e.target.value })}
              style={{ ...inputStyle, width: 100, flex: '0 0 100px', textAlign: 'center', fontWeight: 600 }}
              aria-label="Currency"
            />
            <div style={{ flex: 1, padding: '8px 12px', fontSize: 11, color: 'var(--text-hint)', fontStyle: 'italic', border: '1px dashed var(--border-subtle)', borderRadius: 6, display: 'flex', alignItems: 'center' }}>
              SO number auto-generated (SO-YYYYMMDD-NNNN)
            </div>
          </div>
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: 'var(--text-secondary)' }}>
              LINE ITEMS {selectedMSA && '(from rate card)'}
            </div>
            {form.lines.map((l, i) => {
              if (selectedMSA) {
                // MSA line picker: item_code dropdown, qty + discount only; price is read-only from rate card
                const picked = selectedRateCard.find(r => r.item_code === l.product_code);
                const qty = parseFloat(l.quantity) || 0;
                const disc = parseFloat(l.discount_pct) || 0;
                const unitPrice = picked?.unit_price || 0;
                const lineNet = qty * unitPrice * (1 - disc / 100);
                return (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                    <select
                      title="Rate card item"
                      aria-label={`Line ${i + 1} item from rate card`}
                      value={l.product_code}
                      onChange={e => {
                        const code = e.target.value;
                        const rc = selectedRateCard.find(r => r.item_code === code);
                        const lines = [...form.lines];
                        lines[i] = {
                          ...lines[i],
                          product_code: code,
                          description: rc?.description || '',
                          unit_price: rc ? String(rc.unit_price) : '',
                          gl_account: rc?.gl_account || '',
                        };
                        setForm({ ...form, lines });
                      }}
                      style={{ ...inputStyle, flex: 3, cursor: 'pointer' }}
                    >
                      <option value="">— Pick item —</option>
                      {selectedRateCard.map(r => (
                        <option key={r.id} value={r.item_code}>
                          {r.item_code} • {r.unit_of_measure || 'unit'} • ${r.unit_price.toFixed(2)} • {(r.description || '').slice(0, 50)}
                        </option>
                      ))}
                    </select>
                    <input placeholder="Qty" type="number" value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                    <input placeholder="Disc %" type="number" value={l.discount_pct} onChange={e => updateLine(i, 'discount_pct', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                    <div style={{ flex: 1, textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)', paddingRight: 4 }}>
                      {picked ? `$${lineNet.toFixed(2)}` : '—'}
                    </div>
                  </div>
                );
              }
              // Non-MSA: free-text inputs (existing behavior)
              return (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  <input placeholder="Product" value={l.product_code} onChange={e => updateLine(i, 'product_code', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  <input placeholder="Description" value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} style={{ ...inputStyle, flex: 3 }} />
                  <input placeholder="Qty" type="number" value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  <input placeholder="Price" type="number" value={l.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  <input placeholder="Disc %" type="number" value={l.discount_pct} onChange={e => updateLine(i, 'discount_pct', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                  <input placeholder="GL" value={l.gl_account} onChange={e => updateLine(i, 'gl_account', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                </div>
              );
            })}
            <button onClick={addLine} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>+ Add Line</button>
          </div>

          {/* Phase 3c: live draw-down preview when MSA selected */}
          {selectedMSA && (() => {
            const soTotal = form.lines.reduce((sum, l) => {
              const rc = selectedRateCard.find(r => r.item_code === l.product_code);
              const price = rc?.unit_price || 0;
              const q = parseFloat(l.quantity) || 0;
              const d = parseFloat(l.discount_pct) || 0;
              return sum + q * price * (1 - d / 100);
            }, 0);
            const committed = selectedMSA.committed_amount || 0;
            const consumed = selectedMSA.consumed_amount || 0;
            const after = consumed + soTotal;
            const pct = committed > 0 ? (after / committed) * 100 : 0;
            const warning = pct >= 100 ? 'EXCEEDED' : pct >= 95 ? 'NEAR_CEILING' : pct >= 80 ? 'APPROACHING_CEILING' : null;
            return (
              <div style={{ marginTop: 8, padding: 12, borderRadius: 6, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                  Draw-Down Preview
                </div>
                <DrawdownBar pctUsed={pct} warning={warning as any} height={10} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-secondary)', marginTop: 8 }}>
                  <span>Before: {formatAmount(consumed)}</span>
                  <span>This SO: <strong>{formatAmount(soTotal)}</strong></span>
                  <span>After: <strong>{formatAmount(after)}</strong></span>
                  <span>Ceiling: {formatAmount(committed)}</span>
                </div>
              </div>
            );
          })()}
        </div>
      </Modal>

      {/* Phase 3c: Draw-down preview modal (after successful create) */}
      <Modal open={!!drawdownPreview} onClose={() => setDrawdownPreview(null)} title="SO Created — Draw-down Preview" footer={<button onClick={() => setDrawdownPreview(null)} style={{ padding: '6px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>OK</button>}>
        {drawdownPreview && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Sales Order is DRAFT. Draw-down will be applied to the MSA when you confirm the SO.
            </div>
            <KpiGrid columns={3} items={[
              { label: 'Committed', value: formatAmountCompact(drawdownPreview.committed) },
              { label: 'Consumed After', value: formatAmountCompact(drawdownPreview.consumed_after), color: 'var(--accent)' },
              { label: 'Utilization', value: `${drawdownPreview.pct_used.toFixed(1)}%`, color: drawdownPreview.warning === 'EXCEEDED' ? 'var(--negative)' : drawdownPreview.warning ? 'var(--warning)' : 'var(--positive)' },
            ]} />
            <DrawdownBar pctUsed={drawdownPreview.pct_used} warning={drawdownPreview.warning as any} height={12} />
          </div>
        )}
      </Modal>

      {/* Phase 3c: Draw-down confirmation modal (after confirm_so) */}
      <Modal open={!!drawdownConfirm} onClose={() => setDrawdownConfirm(null)} title="MSA Draw-down Applied" footer={<button onClick={() => setDrawdownConfirm(null)} style={{ padding: '6px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>OK</button>}>
        {drawdownConfirm && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              SO confirmed. Draw-down applied atomically with POB creation.
            </div>
            <KpiGrid columns={3} items={[
              { label: 'Consumed', value: formatAmountCompact(drawdownConfirm.consumed_after), color: 'var(--accent)' },
              { label: 'Utilization', value: `${drawdownConfirm.pct_used.toFixed(1)}%`, color: drawdownConfirm.warning === 'EXCEEDED' ? 'var(--negative)' : drawdownConfirm.warning ? 'var(--warning)' : 'var(--positive)' },
              { label: 'POBs Created', value: drawdownConfirm.pobs_created ?? 0, color: 'var(--positive)' },
            ]} />
            <DrawdownBar pctUsed={drawdownConfirm.pct_used} warning={drawdownConfirm.warning as any} height={12} />
            {(drawdownConfirm.schedules_created ?? 0) > 0 && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {drawdownConfirm.schedules_created} recognition schedule(s) generated for RATABLE POBs.
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Credit Check Modal */}
      <Modal open={!!showCredit} onClose={() => setShowCredit(null)} title="Credit Check Result" footer={<button onClick={() => setShowCredit(null)} style={{ padding: '6px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-default)', borderRadius: 6, cursor: 'pointer' }}>Close</button>}>
        {showCredit && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div style={{ padding: 12, borderRadius: 8, background: showCredit.passed ? 'var(--positive-bg, #f0fdf4)' : 'var(--negative-bg, #fef2f2)', border: `1px solid ${showCredit.passed ? 'var(--positive)' : 'var(--negative)'}`, textAlign: 'center', fontWeight: 700, color: showCredit.passed ? 'var(--positive)' : 'var(--negative)' }}>
              {showCredit.passed ? 'CREDIT CHECK PASSED' : 'CREDIT LIMIT EXCEEDED'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Credit Limit', value: formatAmount(showCredit.credit_limit) },
                { label: 'Outstanding AR', value: formatAmount(showCredit.outstanding_ar) },
                { label: 'Open SO Total', value: formatAmount(showCredit.open_so_total) },
                { label: 'Total Exposure', value: formatAmount(showCredit.total_exposure) },
                { label: 'Headroom', value: formatAmount(showCredit.headroom) },
              ].map(r => (
                <div key={r.label} style={{ padding: '8px 12px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border-subtle)' }}>
                  <div style={{ fontSize: 9, color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>{r.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{r.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
